import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { api } from "@/lib/api";
import {
  ArrowLeft,
  MapPin,
  Calendar,
  Ticket,
  Activity,
  Printer,
  Trash2,
  CheckCircle2,
  Hourglass,
} from "lucide-react";

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
function formatTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString();
  } catch {
    return iso;
  }
}

export default function EventDetail() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [stats, setStats] = useState(null);
  const [feed, setFeed] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("all"); // all | issued | attended
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    try {
      const [ev, st, fd, tk] = await Promise.all([
        api.get(`/events/${id}`),
        api.get(`/events/${id}/stats`),
        api.get(`/events/${id}/scan-feed?limit=30`),
        api.get(`/events/${id}/tickets`),
      ]);
      setEvent(ev.data);
      setStats(st.data);
      setFeed(fd.data);
      setTickets(tk.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    const t = setInterval(async () => {
      try {
        const [st, fd, tk] = await Promise.all([
          api.get(`/events/${id}/stats`),
          api.get(`/events/${id}/scan-feed?limit=30`),
          api.get(`/events/${id}/tickets`),
        ]);
        setStats(st.data);
        setFeed(fd.data);
        setTickets(tk.data);
      } catch {
        /* silent */
      }
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm("Delete this event and all its tickets? This cannot be undone.")) return;
    await api.delete(`/events/${id}`);
    window.location.href = "/admin";
  };

  if (loading || !event || !stats) {
    return (
      <div className="min-h-screen bg-[#F4F4F5]">
        <TopBar />
        <div className="flex justify-center py-20" data-testid="event-loading">
          <div className="brut-spinner" />
        </div>
      </div>
    );
  }

  const filteredTickets =
    filter === "all" ? tickets : tickets.filter((t) => t.status === filter);

  return (
    <div className="min-h-screen bg-[#F4F4F5]" data-testid="event-detail-page">
      <TopBar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 mono-label mb-6 hover:text-[#002FA7]"
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>

        {/* Event header */}
        <div className="mb-10">
          <div className="mono-label text-[#666] mb-2">// EVENT</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter mb-4 break-words">
            {event.name}
          </h1>
          {event.description && (
            <p className="text-[#444] max-w-3xl mb-4">{event.description}</p>
          )}
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <span className="flex items-center gap-2">
              <MapPin className="w-4 h-4" /> {event.venue}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {formatDateTime(event.start_at)} → {formatDateTime(event.end_at)}
            </span>
            <span className="flex items-center gap-2">
              <Ticket className="w-4 h-4" />
              Capacity {event.capacity}
            </span>
          </div>
          <div className="flex flex-wrap gap-3 mt-5">
            <Link
              to={`/admin/events/${id}/tickets`}
              className="brut-btn-accent inline-flex items-center gap-2"
              data-testid="print-tickets-btn"
            >
              <Printer className="w-4 h-4" /> Print / Distribute Tickets
            </Link>
            <button
              type="button"
              onClick={handleDelete}
              className="brut-btn-secondary inline-flex items-center gap-2 hover:!bg-[#FF3B30] hover:!text-white"
              data-testid="delete-event-btn"
            >
              <Trash2 className="w-4 h-4" /> Delete Event
            </button>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <BigStat label="Total Tickets" value={stats.total} testid="stat-total" />
          <BigStat
            label="Attended"
            value={stats.attended}
            color="#00C853"
            testid="stat-attended"
          />
          <BigStat
            label="Remaining"
            value={stats.remaining}
            color="#002FA7"
            testid="stat-remaining"
          />
          <BigStat
            label="Attendance"
            value={`${stats.attendance_rate}%`}
            testid="stat-rate"
          />
        </div>

        {/* Body grid: tickets + feed */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Tickets list */}
          <section className="lg:col-span-2 brut-card p-0 overflow-hidden" data-testid="tickets-section">
            <div className="border-b-2 border-[#121212] p-4 sm:p-6 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
              <div>
                <div className="mono-label text-[#666]">// TICKETS</div>
                <h2 className="font-display text-2xl font-bold uppercase tracking-tight">
                  Ticket Roster
                </h2>
              </div>
              <div className="flex gap-2" data-testid="ticket-filter">
                {["all", "issued", "attended"].map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={
                      "px-3 py-2 mono-label border-2 border-[#121212] " +
                      (filter === f
                        ? "bg-[#121212] text-white"
                        : "bg-white hover:bg-[#F4F4F5]")
                    }
                    data-testid={`filter-${f}`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>
            <div className="max-h-[480px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F4F5] sticky top-0">
                  <tr className="border-b-2 border-[#121212]">
                    <th className="text-left px-4 py-3 mono-label">#</th>
                    <th className="text-left px-4 py-3 mono-label">Code</th>
                    <th className="text-left px-4 py-3 mono-label">Status</th>
                    <th className="text-left px-4 py-3 mono-label">Scanned At</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTickets.map((t) => (
                    <tr
                      key={t.id}
                      className="border-b border-[#E4E4E7] hover:bg-[#F4F4F5]"
                      data-testid={`ticket-row-${t.serial}`}
                    >
                      <td className="px-4 py-3 font-mono">#{t.serial}</td>
                      <td className="px-4 py-3 font-mono">{t.code}</td>
                      <td className="px-4 py-3">
                        {t.status === "attended" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#00C853] text-white border-2 border-[#121212] mono-label">
                            <CheckCircle2 className="w-3 h-3" /> Attended
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-white border-2 border-[#121212] mono-label">
                            <Hourglass className="w-3 h-3" /> Issued
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-[#666]">
                        {t.scanned_at ? formatDateTime(t.scanned_at) : "—"}
                      </td>
                    </tr>
                  ))}
                  {filteredTickets.length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-center text-[#666] py-10 font-mono">
                        No tickets in this view.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* Live scan feed */}
          <section className="brut-card p-0 overflow-hidden" data-testid="scan-feed-section">
            <div className="border-b-2 border-[#121212] p-4 sm:p-6 flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-[#00C853] animate-pulse" />
              <div>
                <div className="mono-label text-[#666]">// LIVE</div>
                <h2 className="font-display text-2xl font-bold uppercase tracking-tight flex items-center gap-2">
                  <Activity className="w-5 h-5" /> Scan Feed
                </h2>
              </div>
            </div>
            <div className="max-h-[480px] overflow-y-auto">
              {feed.length === 0 ? (
                <div className="p-8 text-center text-[#666] font-mono text-sm" data-testid="feed-empty">
                  Awaiting first scan…
                </div>
              ) : (
                <ul>
                  {feed.map((item) => (
                    <li
                      key={item.ticket_id}
                      className="border-b border-[#E4E4E7] p-4 hover:bg-[#F4F4F5]"
                      data-testid={`feed-item-${item.ticket_id}`}
                    >
                      <div className="flex items-baseline justify-between gap-2 mb-1">
                        <span className="font-mono font-bold text-base">
                          #{item.serial}
                        </span>
                        <span className="font-mono text-xs text-[#666]">
                          {formatTime(item.scanned_at)}
                        </span>
                      </div>
                      <div className="font-mono text-xs text-[#666] truncate">
                        {item.code}
                      </div>
                      <div className="mono-label text-[#002FA7] mt-1">
                        by {item.scanned_by_name}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

function BigStat({ label, value, color, testid }) {
  return (
    <div className="brut-card p-5" data-testid={testid}>
      <div className="mono-label text-[#666] mb-2">{label}</div>
      <div
        className="font-display text-4xl lg:text-5xl font-black tabular-nums"
        style={color ? { color } : undefined}
      >
        {value}
      </div>
    </div>
  );
}
